import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_ITEMS, INSTAGRAM_URL, waLink, SITE_URL } from "@/lib/site";

const LEGAL_TEXT = {
  "Política de Privacidade":
    "A Realiza Engenharia trata os dados informados por meio deste site (nome, contatos e descrição da solicitação) exclusivamente para atendimento às solicitações de orçamento e avaliação técnica, nos termos da Lei Geral de Proteção de Dados (LGPD — Lei 13.709/2018). Não comercializamos nem compartilhamos seus dados com terceiros. Para exercer seus direitos de acesso, correção ou exclusão, entre em contato pelo WhatsApp (51) 98177-9245.",
  "Termos de Uso":
    "Este site tem caráter institucional e informativo. Os conteúdos, imagens de projetos e materiais aqui apresentados pertencem à Realiza Engenharia e não podem ser reproduzidos sem autorização. Solicitações de orçamento encaminhadas pelo site têm caráter de agendamento de avaliação técnica e não constituem proposta contratual.",
};

export default function Footer() {
  const [legal, setLegal] = useState(null);

  return (
    <footer className="relative border-t border-gold/15 bg-navy-900">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div>
            <Logo />
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.26em] text-gold/90">
              Inteligência em Gestão Técnica Condominial
            </p>
            <p className="mt-4 font-serif text-lg italic text-slate-400">
              "Você imagina. A gente realiza."
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                aria-label="WhatsApp da Realiza Engenharia"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all duration-300 hover:border-gold hover:text-gold"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                aria-label="Instagram @realiza.predial"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all duration-300 hover:border-gold hover:text-gold"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          <nav aria-label="Links do rodapé">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Navegação</p>
            <ul className="mt-5 space-y-2.5">
              {NAV_ITEMS.map((item) => (
                <li key={item.href}>
                  <a href={item.href} data-testid={`footer-link-${item.href.replace("#", "")}`} className="text-sm text-slate-400 transition-colors hover:text-gold">
                    {item.label.charAt(0) + item.label.slice(1).toLowerCase()}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Serviços</p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-400">
              {["Recuperação de fachadas", "Pintura e textura", "Impermeabilização", "Trabalho em altura", "Fiscalização de obras", "Consultoria para síndicos"].map((s) => (
                <li key={s}>
                  <a href="#servicos" data-testid={`footer-service-${s.split(" ")[0].toLowerCase()}`} className="transition-colors hover:text-gold">{s}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Contato</p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-400">
              <li><a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-contact-whatsapp" className="transition-colors hover:text-gold">WhatsApp: (51) 98177-9245</a></li>
              <li><a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-contact-instagram" className="transition-colors hover:text-gold">Instagram: @realiza.predial</a></li>
              <li><a href={SITE_URL} data-testid="footer-contact-site" className="transition-colors hover:text-gold">realizaengenhariapredial.com</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 text-center sm:flex-row sm:text-left lg:px-8">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Realiza Engenharia — Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-5 text-xs text-slate-500">
            {Object.keys(LEGAL_TEXT).map((key) => (
              <button
                key={key}
                onClick={() => setLegal(key)}
                data-testid={`footer-legal-${key.split(" ")[0].toLowerCase()}`}
                className="transition-colors hover:text-gold"
              >
                {key}
              </button>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {legal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-950/92 p-5 backdrop-blur-md"
            onClick={() => setLegal(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
              className="relative max-h-[80vh] w-full max-w-xl overflow-y-auto border border-gold/25 bg-navy-900 p-8"
            >
              <button
                onClick={() => setLegal(null)}
                aria-label="Fechar"
                data-testid="footer-legal-close"
                className="absolute right-5 top-5 text-slate-400 transition-colors hover:text-gold"
              >
                <X className="h-5 w-5" />
              </button>
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gold">{legal}</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{LEGAL_TEXT[legal]}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
}
